// Tab Dados: identidade do merchant.
//
// Tudo READ-ONLY nesta onda — nome, CNPJ, nome fantasia, e-mail e sócio são
// dados legais e o backend os rejeita no PUT por contrato. A edição do que É
// mutável (ativo, categoria, marca) entra na onda seguinte.
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { AdminMerchant } from "@/types/admin-merchant"

import { formatDocument } from "../utils"
import { ReadOnlyField } from "./read-only-field"

const panelPrefix = "pages.adminMerchants"

type MerchantDataTabProps = {
  merchant: AdminMerchant
}

export const MerchantDataTab = ({
  merchant,
}: MerchantDataTabProps) => {
  const { t } = useTranslation()
  const active = Boolean(merchant.active)

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-card p-4 shadow-sm">
        <h2 className="mb-4 text-sm font-semibold">
          {t(`${panelPrefix}.data.identity`)}
        </h2>
        <div className="grid grid-cols-1 gap-4 @2xl:grid-cols-2 @5xl:grid-cols-3">
          <ReadOnlyField
            label={t(`${panelPrefix}.fields.name`)}
            value={merchant.name}
          />
          <ReadOnlyField
            label={t(`${panelPrefix}.fields.fantasyName`)}
            value={merchant.fantasyName}
          />
          <ReadOnlyField
            label={t(`${panelPrefix}.fields.document`)}
            value={formatDocument(merchant.document)}
            mono
          />
          <ReadOnlyField
            label={t(`${panelPrefix}.fields.email`)}
            value={merchant.email}
          />
          <ReadOnlyField
            label={t(`${panelPrefix}.fields.type`)}
            value={merchant.type}
          />
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              {t(`${panelPrefix}.fields.status`)}
            </span>
            <Badge
              variant="outline"
              className={cn(
                "w-fit rounded-full border font-medium",
                active
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-zinc-200 bg-zinc-50 text-zinc-500"
              )}
            >
              {t(
                `${panelPrefix}.${active ? "badgeActive" : "badgeInactive"}`
              )}
            </Badge>
          </div>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          {t(`${panelPrefix}.data.immutableNote`)}
        </p>
      </section>

      <section className="rounded-xl border bg-card p-4 shadow-sm">
        <h2 className="mb-4 text-sm font-semibold">
          {t(`${panelPrefix}.data.classification`)}
        </h2>
        <div className="grid grid-cols-1 gap-4 @2xl:grid-cols-2 @5xl:grid-cols-3">
          <ReadOnlyField
            label={t(`${panelPrefix}.fields.category`)}
            value={merchant.category?.name}
          />
          <ReadOnlyField
            label={t(`${panelPrefix}.fields.brand`)}
            value={merchant.brand?.name}
          />
          <ReadOnlyField
            label={t(`${panelPrefix}.fields.owner`)}
            value={merchant.storeOwner?.name}
          />
        </div>
      </section>
    </div>
  )
}
